<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Color } from '@previo/codebooks'
import { QField } from 'quasar'
import dayjs, { type Dayjs } from 'dayjs'
import { usePrevioDS } from '../../../composables/helpers'
import { $t } from '../../../composables/i18n'
import { type PValidationRule } from '../../../types/types'
import InputFieldLabel from '../components/LabelInputField.vue'
import PContextMenu from '../../context-menu/PContextMenu.vue'
import PIcon from '../../icon/PIcon.vue'
import { PTimeInputFieldIncrement } from '../types'
import PBtn from '../../btn/PBtn.vue'
import { PContextMenuPositionEnum } from '../../context-menu/types'
import { type TimeEntry } from './types'
import TimeEntryList from './components/time-input-field/TimeEntryList.vue'
import TimePickerDrumContent from './components/time-input-field/drum/TimePickerDrumContent.vue'
import { useTimeRangeValidator } from './composables/rules'
import FormatSegmentBuilder from './components/segments/FormatSegmentBuilder.vue'
import { ErrorState } from './components/segments/model/ErrorState'

type Props = {
  label?: null | string
  tooltip?: null | string
  hint?: null | string
  increment?: PTimeInputFieldIncrement
  rules?: PValidationRule[]
  min?: Dayjs
  max?: Dayjs
  strict?: boolean
  disable?: boolean
  readonly?: boolean
  disableClearable?: boolean
  autofocus?: boolean
  placeholder?: null | string
  cy: string
}

const props = withDefaults(defineProps<Props>(), {
  increment: PTimeInputFieldIncrement.QUARTER,
  rules() {
    return []
  },
  strict: false,
  disable: false,
  readonly: false,
  disableClearable: false,
  autofocus: false,
  placeholder: null,
})

const ds = usePrevioDS()

const model = defineModel<Dayjs | null>()
const isTimePickerVisible = ref<boolean>(false)
const qFieldRef = ref<InstanceType<typeof QField>>()
const errorState = ref<ErrorState>(new ErrorState())
const qError = ref<boolean>(false)
const qErrorMessage = ref<string>('')

const timeEntries = computed<TimeEntry[]>(() => {
  const entries: TimeEntry[] = []

  const currentDate = (model.value || dayjs()).tz().startOf('day')

  if (props.min && props.max) {
    const minDate = props.min.tz().startOf('day')
    const maxDate = props.max.tz().startOf('day')

    if (currentDate.isBefore(minDate) || currentDate.isAfter(maxDate)) {
      return []
    }
  }

  let startHour = 0
  let startMinute = 0
  let endHour = 23
  let endMinute = 59

  if (props.min && currentDate.isSame(props.min.tz().startOf('day'), 'day')) {
    startHour = props.min.tz().hour()
    startMinute = props.min.tz().minute()
  }

  if (props.max && currentDate.isSame(props.max.tz().startOf('day'), 'day')) {
    endHour = props.max.tz().hour()
    endMinute = props.max.tz().minute()
  }

  const startMinutes = startHour * 60 + startMinute
  const endMinutes = endHour * 60 + endMinute
  const startSlot = Math.ceil(startMinutes / props.increment)
  const endSlot = Math.floor(endMinutes / props.increment)

  for (let slot = startSlot; slot <= endSlot; slot++) {
    const totalMinutes = slot * props.increment
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (hours >= 24 || totalMinutes < startMinutes || totalMinutes > endMinutes) {
      continue
    }

    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    entries.push({ text: timeString, value: timeString })
  }

  return entries
})

const disable = computed<boolean>(() => props.disable)

const isRequired = computed<boolean>(() => {
  return props.rules.includes(ds.rules.required)
})

const validatorModel = computed<Dayjs | null>(() => model.value ?? null)

const validateTimeRange = useTimeRangeValidator(validatorModel, props.min, props.max)

const validateIncrement: PValidationRule = (value: Dayjs | null): string | boolean => {
  if (!props.strict) {
    return true
  }

  if (!value) {
    return $t('The value cannot be empty')
  }

  const timeStr = value.tz().format('HH:mm')
  const isValid = timeEntries.value.some((entry: TimeEntry) => entry.value === timeStr)

  return (
    isValid ||
    $t('The selected time must be a multiple of {increment}', {
      increment: props.increment,
    })
  )
}

const qRules = computed<PValidationRule[]>(() => {
  const rules: PValidationRule[] = []

  if (isRequired.value || props.disableClearable) {
    rules.push((val: Dayjs | null) => val !== null || $t('The value cannot be empty'))
  }

  return [...rules, ...props.rules, validateIncrement, validateTimeRange]
})

const openPicker = (): void => {
  isTimePickerVisible.value = true
}

const closePicker = (): void => {
  isTimePickerVisible.value = false
}

const togglePicker = (): void => {
  isTimePickerVisible.value = !isTimePickerVisible.value
}

const onTimeEntryClick = (): void => {
  closePicker()
}

const onSegmentPickerClick = (): void => {
  if (!ds.screen().isMobile || props.readonly) {
    return
  }

  openPicker()
}

watch(
  errorState,
  () => {
    qError.value = errorState.value.hasError
    qErrorMessage.value = errorState.value.errorMessage
  },
  { deep: true },
)

watch(
  isTimePickerVisible,
  (isVisible) => {
    if (!isVisible) {
      return
    }

    nextTick().then(() => {
      document.querySelector('[data-scroll-into-view="true"]')?.scrollIntoView({
        behavior: 'instant',
        block: 'center',
        inline: 'nearest',
      })
    })
  },
  { immediate: true },
)

onMounted(() => {
  qFieldRef.value?.$el.addEventListener('click', (event: PointerEvent) => {
    if (disable.value || props.readonly) {
      return
    }

    if ((event.target as HTMLElement).closest('.q-field__append')) {
      return
    }

    if (
      ds.screen().isMobile &&
      (event.target as HTMLElement).closest('.ds-format-segment-builder__wrapper')
    ) {
      return
    }

    if ((event.target as HTMLElement).closest('.ds-segment')) {
      closePicker()
      return
    }

    togglePicker()
  })
})
</script>

<template>
  <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-field
    ref="qFieldRef"
    v-model="model"
    :bg-color="Color.WHITE"
    :rules="qRules"
    :autofocus="autofocus"
    :disable="disable"
    :hint="hint ?? undefined"
    :data-cy="cy"
    :readonly="readonly || ds.screen().isMobile"
    :class="{ 'ds-input-field--readonly': readonly }"
    :error="qError"
    :error-message="qErrorMessage"
    outlined
    no-error-icon
    hide-bottom-space
  >
    <template #default>
      <format-segment-builder
        v-model="model"
        v-model:error-state="errorState"
        format="HH:mm"
        :disable-clearable="disableClearable"
        :minute-increment="increment"
        :readonly="readonly"
        @click="onSegmentPickerClick"
      />

      <p-context-menu
        v-model="isTimePickerVisible"
        :title="$t('Select time')"
        :anchor="PContextMenuPositionEnum.BOTTOM_START"
        :self="PContextMenuPositionEnum.TOP_START"
        cy="time-input-field-context-menu"
        class="ds-time-picker-context-menu"
      >
        <template v-if="ds.screen().isMobile">
          <time-picker-drum-content v-model="model" :increment="increment" />

          <p-btn
            :text="$t('Confirm')"
            cy="time-input-field-confirm-btn"
            grow
            @click="closePicker"
          />
        </template>

        <template v-else>
          <time-entry-list
            v-model="model"
            :time-entries="timeEntries"
            @time-selected="onTimeEntryClick"
          />
        </template>
      </p-context-menu>
    </template>

    <template #append>
      <p-icon
        v-if="!disableClearable && model !== null && !readonly"
        icon="xmark-large"
        cursor-pointer
        :size="15"
        @click.stop="model = null"
      />
    </template>
  </q-field>
</template>
